import type { StationeryPreviewImage } from '~~/layers/base/types/stationeryPreviewImage'
import { HANDWRITTEN_DENORMALIZED_PREVIEW_META_KEY } from '~~/layers/base/utils/handwritingProjectDenormalizedPreview'

/**
 * Convert a `data:` URL produced by the handwritten hero Fabric preview into a `Blob` for multipart upload.
 * Uses `fetch` so both base64 and non-base64 data URLs are supported in browsers.
 */
export async function handwrittenPreviewDataUrlToBlob(dataUrl: string): Promise<Blob | null> {
    if (!dataUrl.startsWith('data:')) {
        return null
    }
    try {
        const res = await fetch(dataUrl)
        return await res.blob()
    } catch {
        return null
    }
}

/**
 * Cheap in-memory fingerprint for deduping repeat preview uploads (same session).
 * Not cryptographic; avoids storing full multi‑MB data URLs for equality checks.
 */
export function fingerprintHandwrittenPreviewDataUrl(dataUrl: string): string {
    let h = 2166136261
    const step = Math.max(1, Math.floor(dataUrl.length / 4096))
    for (let i = 0; i < dataUrl.length; i += step) {
        h ^= dataUrl.charCodeAt(i)!
        h = Math.imul(h, 16777619)
    }
    return `${dataUrl.length}:${h >>> 0}`
}

/**
 * Build one `StationeryPreviewImage` for `handwriting_project.preview_image` from hero metadata + Cloudflare id.
 * Shape matches a single entry from `handwriting_stationery.preview_images`.
 */
/**
 * Session-only card-front preview row using a Fabric `data:` URL as `external_url`.
 * Used when pushing the project into the store on Send so the success screen does not wait on Cloudflare.
 */
export function buildHandwrittenHeroPreviewEntryFromDataUrl(
    dataUrl: string,
    meta: Record<string, unknown>
): StationeryPreviewImage {
    const raw = meta.partName
    const label = typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : 'hero'
    return {
        label,
        external_url: dataUrl,
        meta: {
            ...meta,
            [HANDWRITTEN_DENORMALIZED_PREVIEW_META_KEY]: 'card_front',
        },
    }
}

export function buildHandwrittenHeroPreviewEntry(
    cloudflareImageId: string,
    meta: Record<string, unknown>
): StationeryPreviewImage {
    const raw = meta.partName
    const label = typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : 'hero'
    return {
        label,
        cloudflare_image_id: cloudflareImageId,
        meta: {
            ...meta,
            [HANDWRITTEN_DENORMALIZED_PREVIEW_META_KEY]: 'card_front',
        },
    }
}

/**
 * Session-only envelope-face preview row using a Fabric `data:` URL as `external_url`.
 * Not persisted to the server; overlays Cloudflare rows in the client store cache on Send.
 */
export function buildHandwrittenEnvelopeFacePreviewEntryFromDataUrl(
    dataUrl: string,
    meta: Record<string, unknown>
): StationeryPreviewImage {
    const raw = meta.partName ?? meta.part_name
    const label = typeof raw === 'string' && raw.trim().length > 0 ? String(raw).trim() : 'Face'
    return {
        label,
        name: 'Face',
        external_url: dataUrl,
        meta: {
            ...meta,
            partName: 'Envelope',
            surface_id: 3,
            surfaceId: 3,
            [HANDWRITTEN_DENORMALIZED_PREVIEW_META_KEY]: 'envelope_face',
        },
    }
}

/** Denormalized envelope **face** with addresses (saved on `handwriting_project.preview_image` array). */
export function buildHandwrittenEnvelopeFacePreviewEntry(
    cloudflareImageId: string,
    meta: Record<string, unknown>
): StationeryPreviewImage {
    const raw = meta.partName ?? meta.part_name
    const label = typeof raw === 'string' && raw.trim().length > 0 ? String(raw).trim() : 'Face'
    return {
        label,
        name: 'Face',
        cloudflare_image_id: cloudflareImageId,
        meta: {
            ...meta,
            partName: 'Envelope',
            surface_id: 3,
            surfaceId: 3,
            [HANDWRITTEN_DENORMALIZED_PREVIEW_META_KEY]: 'envelope_face',
        },
    }
}
